import type { ThemeColor } from '@frontend/components';

import type { BadgeColorScheme, BadgeStyleConfig } from './types';

const COLOR_SCHEME_TO_COLOR_NAMES_MAP: Record<
  BadgeColorScheme,
  { mainColor: ThemeColor; lightColor: ThemeColor; featureColor: ThemeColor }
> = {
  success: {
    mainColor: 'alert.success',
    lightColor: 'alert.surfaceSuccess',
    featureColor: 'feature.onInteraction',
  },
  error: {
    mainColor: 'alert.error',
    lightColor: 'alert.surfaceError',
    featureColor: 'feature.onInteraction',
  },
  warning: {
    mainColor: 'alert.warning',
    lightColor: 'alert.surfaceWarning',
    featureColor: 'feature.onInteraction',
  },
  info: {
    mainColor: 'alert.info',
    lightColor: 'alert.surfaceInfo',
    featureColor: 'feature.onInteraction',
  },
  select: {
    mainColor: 'feature.primary',
    lightColor: 'page.plateMedium',
    featureColor: 'feature.primary',
  },
  selectFixed: {
    mainColor: 'feature.secondary',
    lightColor: 'page.plateMedium',
    featureColor: 'feature.primary',
  },
  assistance: {
    mainColor: 'alert.assistance',
    lightColor: 'interaction.surfaceSuggestionActive',
    featureColor: 'alert.assistance',
  },
};

const BASE_STYLE = {
  fontFamily: 'default',
  textTransform: 'uppercase',
  fontSize: 'sm',
  borderRadius: 'sm',
  fontWeight: 'heavy',
  border: '1px solid',
};

const SIZES = {
  xs: {
    px: '0.125rem',
    fontSize: '10px',
    lineHeight: '0.85rem',
  },
  sm: {
    px: '0.25rem',
    fontSize: 'sm',
    lineHeight: '1rem',
  },
  md: {
    px: '0.3125rem',
    fontSize: 'md',
    lineHeight: '1.125rem',
  },
  lg: {
    px: '0.375rem',
    fontSize: 'lg',
    lineHeight: '1.375rem',
  },
};

const VARIANTS: BadgeStyleConfig['variants'] = {
  solid: ({ colorScheme }) => {
    const colorNames = COLOR_SCHEME_TO_COLOR_NAMES_MAP[colorScheme];

    return {
      bg: colorNames.mainColor,
      borderColor: colorNames.mainColor,
      color: colorNames.featureColor,
    };
  },
  subtle: ({ colorScheme }) => {
    const colorNames = COLOR_SCHEME_TO_COLOR_NAMES_MAP[colorScheme];

    return {
      bg: colorNames.lightColor,
      borderColor: colorNames.lightColor,
      color: colorNames.mainColor,
    };
  },
  outline: ({ colorScheme }) => {
    const colorNames = COLOR_SCHEME_TO_COLOR_NAMES_MAP[colorScheme];

    return {
      bg: 'transparent',
      borderColor: colorNames.mainColor,
      color: colorNames.mainColor,
    };
  },
};

const DEFAULT_PROPS = {
  size: 'sm',
  colorScheme: 'info',
  variant: 'solid',
} as const;

export const BADGE_STYLE_CONFIG: BadgeStyleConfig = {
  baseStyle: BASE_STYLE,
  sizes: SIZES,
  variants: VARIANTS,
  defaultProps: DEFAULT_PROPS,
};
